/**
 * Nhận định cạnh biểu đồ (P-INS): 2–3 ý ngắn sinh từ số liệu kỳ đang xem bằng LUẬT TÍNH SẴN
 * (không cần khoá API, không gọi mô hình). Thuần hàm, không phụ thuộc UI — Lys (N) gọi `deriveInsights`
 * với số đang vẽ; khi có khoá, tầng mô hình chỉ viết lại câu, số và nguồn vẫn do code này giữ.
 */
import type { Cite } from './types';

export type InsightKind = 'peak' | 'trough' | 'vsPrev' | 'imbalance' | 'concentration' | 'belowThreshold';

export interface InsightSeries {
  /** Khoá ổn định của biểu đồ (vd `jobs`). */
  key: string;
  /** Tên hiển thị của chỉ số (vd "Số job"). */
  label: string;
  /** Nhãn từng kỳ, cùng độ dài `values` (vd "T10"). */
  periods: readonly string[];
  values: readonly number[];
  /** Chỉ số tiền (doanh thu, giá vốn, lợi nhuận…): bị bỏ nếu người xem không có quyền tiền. */
  money?: boolean;
  /** Đơn vị ngắn gắn sau số (vd "job", "TEU", "%"). */
  unit?: string;
  /** Ngưỡng cảnh báo: dưới `min` hoặc trên `max` → nhận định `belowThreshold`. */
  min?: number;
  max?: number;
  /** Nơi lấy số, để trích nguồn. */
  source: Cite;
}

export interface InsightBreakdown {
  key: string;
  label: string;
  /** Thành phần của một tổng (vd khách hàng theo số job). */
  parts: readonly { name: string; value: number }[];
  money?: boolean;
  unit?: string;
  source: Cite;
}

export interface Insight {
  kind: InsightKind;
  /** Biểu đồ nguồn. */
  key: string;
  text: string;
  /** Mức chú ý: 2 = cần xem ngay, 1 = đáng biết. */
  weight: 1 | 2;
  cite: Cite;
}

export interface InsightInput {
  series?: readonly InsightSeries[];
  breakdowns?: readonly InsightBreakdown[];
  /** Chỉ số kỳ đang xem (mặc định: kỳ cuối). */
  at?: number;
  /** Người xem có quyền nhóm tiền không. Mặc định KHÔNG. */
  canSeeMoney?: boolean;
  /** Tối đa số ý (mặc định 3). */
  max?: number;
}

const num = (v: number): string => {
  const r = Math.round(v * 10) / 10;
  const [a, b] = Math.abs(r).toString().split('.');
  return (r < 0 ? '−' : '') + a!.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + (b ? ',' + b : '');
};
const pct = (v: number): string => num(Math.abs(v)) + '%';
const withUnit = (v: number, u?: string): string => num(v) + (u ? ' ' + u : '');

function seriesInsights(s: InsightSeries, at: number): Insight[] {
  const n = Math.min(s.values.length, s.periods.length);
  if (n === 0 || at < 0 || at >= n) return [];
  const vals = s.values.slice(0, n);
  const cur = vals[at]!;
  const out: Insight[] = [];
  const push = (kind: InsightKind, text: string, weight: 1 | 2) => out.push({ kind, key: s.key, text, weight, cite: s.source });

  if (s.min !== undefined && cur < s.min) push('belowThreshold', `${s.label} ${s.periods[at]} còn ${withUnit(cur, s.unit)}, dưới ngưỡng ${withUnit(s.min, s.unit)}.`, 2);
  if (s.max !== undefined && cur > s.max) push('belowThreshold', `${s.label} ${s.periods[at]} lên ${withUnit(cur, s.unit)}, vượt ngưỡng ${withUnit(s.max, s.unit)}.`, 2);

  if (at > 0) {
    const prev = vals[at - 1]!;
    if (prev !== 0) {
      const d = ((cur - prev) / Math.abs(prev)) * 100;
      if (Math.abs(d) >= 10) push('vsPrev', `${s.label} ${s.periods[at]} ${d > 0 ? 'tăng' : 'giảm'} ${pct(d)} so với ${s.periods[at - 1]} (${num(prev)} → ${num(cur)}).`, Math.abs(d) >= 25 ? 2 : 1);
    }
  }

  if (n >= 3) {
    const max = Math.max(...vals);
    const min = Math.min(...vals);
    if (max !== min) {
      if (cur === max) push('peak', `${s.label} ${s.periods[at]} là đỉnh của ${n} kỳ (${withUnit(cur, s.unit)}).`, 1);
      else if (cur === min) push('trough', `${s.label} ${s.periods[at]} là đáy của ${n} kỳ (${withUnit(cur, s.unit)}).`, 1);
      else {
        const pi = vals.indexOf(max);
        const avg = vals.reduce((a, b) => a + b, 0) / n;
        if (avg > 0 && max / avg >= 1.3 && pi !== at) push('peak', `Đỉnh ${s.label.toLowerCase()} ở ${s.periods[pi]} (${withUnit(max, s.unit)}), cao hơn trung bình ${pct((max / avg - 1) * 100)}.`, 1);
      }
    }
  }
  return out;
}

function breakdownInsights(b: InsightBreakdown): Insight[] {
  const parts = b.parts.filter((p) => p.value > 0);
  const total = parts.reduce((a, p) => a + p.value, 0);
  if (parts.length < 2 || total <= 0) return [];
  const sorted = [...parts].sort((x, y) => y.value - x.value);
  const out: Insight[] = [];
  const top = sorted[0]!;
  const topShare = (top.value / total) * 100;
  const top3 = sorted.slice(0, 3).reduce((a, p) => a + p.value, 0) / total * 100;
  if (topShare >= 40) out.push({ kind: 'concentration', key: b.key, weight: 2, cite: b.source, text: `${top.name} chiếm ${pct(topShare)} ${b.label.toLowerCase()} — tập trung một mối.` });
  else if (parts.length > 3 && top3 >= 70) out.push({ kind: 'concentration', key: b.key, weight: 1, cite: b.source, text: `3 mục đầu (${sorted.slice(0, 3).map((p) => p.name).join(', ')}) chiếm ${pct(top3)} ${b.label.toLowerCase()}.` });
  const last = sorted[sorted.length - 1]!;
  if (last.value > 0 && top.value / last.value >= 3 && parts.length <= 6) out.push({ kind: 'imbalance', key: b.key, weight: 1, cite: b.source, text: `${top.name} gấp ${num(top.value / last.value)} lần ${last.name} (${num(top.value)} so với ${num(last.value)}).` });
  return out;
}

/** 2–3 nhận định, nặng trước; không bao giờ trả ý từ chỉ số tiền khi `canSeeMoney` ≠ true. */
export function deriveInsights(input: InsightInput): Insight[] {
  const money = input.canSeeMoney === true;
  const max = Math.max(1, input.max ?? 3);
  const all: Insight[] = [];
  for (const s of input.series ?? []) {
    if (s.money && !money) continue;
    const at = input.at ?? s.values.length - 1;
    all.push(...seriesInsights(s, at));
  }
  for (const b of input.breakdowns ?? []) {
    if (b.money && !money) continue;
    all.push(...breakdownInsights(b));
  }
  // nặng trước, giữ thứ tự khai; tối đa 1 ý mỗi loại mỗi biểu đồ đã bảo đảm bởi luật; không trùng loại quá 2 lần
  const ranked = all.map((x, i) => ({ x, i })).sort((a, b) => b.x.weight - a.x.weight || a.i - b.i).map((r) => r.x);
  const kindCount: Partial<Record<InsightKind, number>> = {};
  const out: Insight[] = [];
  for (const x of ranked) {
    if ((kindCount[x.kind] ?? 0) >= 2) continue;
    kindCount[x.kind] = (kindCount[x.kind] ?? 0) + 1;
    out.push(x);
    if (out.length >= max) break;
  }
  return out;
}
